import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { useUpdateNodeInternals } from '@xyflow/react'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import pickBy from 'lodash/pickBy'
import { useEffect, useMemo } from 'react'
import type { AgentChannels } from 'v2/types'
import { SCREENING_NODE_TYPES } from '../config/contact-screening.config'
import { FlowHistoryEvent, useFlowHistoryManager } from '../hooks/use-flow-history'
import { useGenerateNodeMetadataOptimistically } from '../hooks/use-generate-node-metadata-optimistically'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { useFlowStore } from '../store'
import { EditorType, FlowNodeTypes, type NodeDataBase, type SidebarComponentProps } from '../types'
import {
  AI_METADATA_KEYS,
  getDefaultNameConfig,
  refreshNodeVars,
  scheduleNodeVariableReferenceRewrite,
  updateSimilarNodesWithSameChannel,
} from '../utils'
import { NodeNameRegenButton } from './node-name-field'

interface GenericSidebarComponentProps<T extends NodeDataBase<unknown>>
  extends SidebarComponentProps<T> {
  fields: GenericFieldConfig<T['config']>
  callback?:
    | ((data: T['config']) => T['config'])
    | ((data: T['config']) => void)
    | ((data: T['config']) => Promise<T['config']>)
  className?: string
  defaultValues?: Partial<T['config']>
}

export const GenericSidebarComponent = <T extends NodeDataBase<unknown>>({
  selectedNode,
  unselectNode,
  fields = [],
  callback,
  className,
  defaultValues,
}: GenericSidebarComponentProps<T>) => {
  // Get base default values from the node's data config
  const nodeDefaultValues = selectedNode.data.config as T['config']

  // Merge defaultValues with nodeDefaultValues, filling in empty fields

  const mergedDefaultValues = useMemo(
    () =>
      merge(
        {},
        nodeDefaultValues,
        pickBy(defaultValues, (value) => value !== undefined && value !== null && value !== ''),
      ),
    [nodeDefaultValues, defaultValues],
  )

  const { updateNodeData, getNodes, getEdges } = useTypedReactFlow()
  const updateNodeInternals = useUpdateNodeInternals()
  const setSidebarFormInstance = useFlowStore((state) => state.setSidebarFormInstance)
  const setSelectedNode = useFlowStore((state) => state.selectNode)
  const isEditing = useFlowStore((state) => state.isEditing)
  const nameFocusNonce = useFlowStore((state) => state.nameFocusNonce)
  const { saveStateToHistory } = useFlowHistoryManager()
  const generateNodeMetadataOptimistically = useGenerateNodeMetadataOptimistically()
  const nameConfig = getDefaultNameConfig()
  // Start nodes are named by their trigger: the card never renders config.name, and
  // getNodeVarsForHandle keys the API-request payload variables on the literal name
  // 'Start' — so no name field is offered (config.name still round-trips via form
  // defaults). AI metadata generation skips start nodes for the same reason.
  const nameFields =
    selectedNode.type === FlowNodeTypes.start
      ? []
      : [
          {
            ...nameConfig,
            inputProps: {
              ...nameConfig.inputProps,
              endIcon: (
                <NodeNameRegenButton
                  nodeId={selectedNode.id}
                  nodeType={selectedNode.type as FlowNodeTypes}
                />
              ),
            },
          },
        ]
  const formFields = [
    ...nameFields,
    ...fields.filter((field) => field.name !== 'name'), // Remove any explicit name fields from component
  ].map((field) => {
    const fieldDisabled = (field as { disabled?: (state: unknown) => boolean | string }).disabled
    return {
      ...field,
      // The edit-mode lock composes with a field's own disabled rule instead of
      // replacing it (a string result keeps its tooltip semantics).
      disabled: (state: unknown) => !isEditing || (fieldDisabled ? fieldDisabled(state) : false),
    }
  }) as GenericFieldConfig<T['config']>

  // Focus + select the name input when a context-menu Rename is requested. Deferred by
  // two frames: the menu closes via a synthetic Escape and Radix restores focus to its
  // trigger on close, which would steal a synchronous focus().
  useEffect(() => {
    if (!nameFocusNonce) return
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLInputElement>('[data-testid="sidebar"] #name')
        input?.focus()
        input?.select()
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [nameFocusNonce])

  // Note: We don't reset hasUnsavedChanges when selecting a new node
  // because unsaved changes are tracked at the flow level, not per node
  // The flag should only be cleared when the flow is actually saved/published

  return (
    <div>
      <GenericForm<T['config']>
        className={cn('gap-4 grid-cols-2', className)}
        defaultValues={mergedDefaultValues}
        fields={formFields}
        onMount={(form) => {
          setSidebarFormInstance(form)
          // Highlight the fields that have issues when the form is mounted
          if (selectedNode.data.meta.issues && selectedNode.data.meta.issues.length > 0) {
            form.validateAllFields('change')
          }
        }}
        id={selectedNode.type || ''}
        onSubmit={async (data) => {
          // selectedNode is memoized on the node ID and goes stale after in-panel node
          // mutations (the Auto-name button applies AI metadata directly) — read live
          // node data for change detection and rename bookkeeping, or a post-✦ save
          // would re-pin the AI's name and trigger a second rename.
          const liveConfig =
            getNodes().find((n) => n.id === selectedNode.id)?.data.config ??
            selectedNode.data.config
          const nodeType = selectedNode.type as FlowNodeTypes
          // Regenerate AI metadata only when node BEHAVIOR changed. Name/description
          // are generation outputs, not inputs: a manual rename or an Auto-name click
          // must not trigger another generation on the next save.
          const behaviorChanged = !isEqual(
            omit(liveConfig, AI_METADATA_KEYS),
            omit(data, AI_METADATA_KEYS),
          )
          // Screening nodes are excluded: their name keys {{name.screening_*}}
          // template reads and the composite's dial-half naming, so it stays
          // exactly what the user typed.
          const nodesToNotGenerateAutomatedMetadata = [FlowNodeTypes.start, ...SCREENING_NODE_TYPES]
          const shouldAutoGenerateMetadata =
            behaviorChanged && !nodesToNotGenerateAutomatedMetadata.includes(nodeType)

          let processedData = data
          if (callback) {
            const result = callback(data)
            if (result instanceof Promise) processedData = await result
            else processedData = result || data
          }
          // The node's own save step can add variables after the click (a fill that lands meanwhile).
          const hasDataChanged =
            !isEqual(liveConfig, data) ||
            !isEqual(get(liveConfig, 'extract_variables'), get(processedData, 'extract_variables'))
          // Store trimmed names: an untrimmed "Foo " passes the uniqueness check
          // against "Foo" and the collision would surface as a backend 500; it also
          // makes two visually identical names possible.
          if (typeof processedData.name === 'string') {
            processedData = { ...processedData, name: processedData.name.trim() }
          }

          // Mark user change for editor tracking when data is modified
          if (hasDataChanged) {
            useFlowStore.getState().markUserChange()
          }

          // A user-typed name pins the node: AI metadata generation keeps pinned names.
          const previousName = liveConfig.name
          const isUserRename = hasDataChanged && processedData.name !== previousName

          updateNodeData(selectedNode.id, (o) => ({
            config: processedData,
            ...(isUserRename ? { name_source: 'user' as const } : {}),
            // Clear any pending issues, because submit happens only if there are no issues
            // Set last_editor_type to USER when user edits a node
            meta: {
              ...o.data.meta,
              issues: [],
              ...(hasDataChanged ? { last_editor_type: EditorType.USER } : {}),
            },
          }))

          // Rewrite {{previousName.var}} references in downstream nodes. The AI path does
          // this inside the generation hook, but a pinned name never goes through it.
          if (isUserRename) {
            scheduleNodeVariableReferenceRewrite(
              selectedNode.id,
              previousName,
              processedData.name,
              updateNodeData,
              () => getEdges(),
            )
          }

          // Feature: Update similar nodes with same channel
          // Check if this is a channel update for AI agent nodes
          if (
            selectedNode.type &&
            [
              FlowNodeTypes.agent_preset_conversation,
              FlowNodeTypes.ai_agent_v2,
              FlowNodeTypes.ai_agent_v29,
            ].includes(selectedNode.type as FlowNodeTypes) &&
            hasDataChanged &&
            'channel' in data &&
            'channel' in selectedNode.data.config
          ) {
            const oldChannel = selectedNode.data.config.channel
            const newChannel = data.channel

            // Only update similar nodes if the channel actually changed and user wants to update similar nodes
            if (
              oldChannel !== newChannel &&
              'update_similar_nodes' in data &&
              data.update_similar_nodes === true
            ) {
              updateSimilarNodesWithSameChannel(
                selectedNode.id,
                oldChannel as AgentChannels,
                newChannel as AgentChannels,
                getNodes,
                updateNodeData,
              )
            }
          }

          if (shouldAutoGenerateMetadata) {
            // !Hack to re-render the sidebar with the updated data
            // !as the useMemo in @apps/web/src/v2/pages/flow/sidebar/index.tsx uses useMemo
            // !with the selctedNodeId as dependency
            unselectNode(true)
            queueMicrotask(() => {
              setSelectedNode(selectedNode.id)
            })

            await generateNodeMetadataOptimistically(
              selectedNode.id,
              nodeType,
              processedData,
              getNodes(),
              updateNodeData,
            )
          }

          // Refresh the node vars if data has changed
          // Use queueMicrotask to ensure there is no performance hit and it is done after the node is updated
          if (hasDataChanged) {
            queueMicrotask(() => refreshNodeVars(selectedNode.id))
          }
          // Callback to perform some action after the form is submitted

          updateNodeInternals(selectedNode.id) // to update the layout
          unselectNode(true)
          saveStateToHistory(FlowHistoryEvent.NodeDataChange)
        }}
        includeSubmitButton={false}
      />
    </div>
  )
}
